/**
 * UserLogin Component
 * Login form with CAPTCHA support + i18n
 * When designColors prop is provided, uses inline styles (custom designs).
 * When designColors is absent, uses isDarkMode Tailwind classes (classic design).
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';
import { deviceHeader, setDeviceId } from '../../utils/deviceId';
import DeviceLimitModal from './DeviceLimitModal';

const UserLogin = ({ API, onSuccess, onSwitchToRegister, isDarkMode, designColors, registerHidden }) => {
  const { t } = useTranslation();
  const dc = designColors; // shorthand, null for classic
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    captchaAnswer: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Device-limit state: shown when /auth/login returns 423
  const [deviceLimitOpen, setDeviceLimitOpen] = useState(false);
  const [activeDevices, setActiveDevices] = useState([]);
  const [deviceLimit, setDeviceLimit] = useState(3);

  // Session-revoked banner: set by App.js when token refresh fails with code=session_revoked
  const [sessionRevokedReason, setSessionRevokedReason] = useState('');

  useEffect(() => {
    loadCaptcha();
    try {
      const reason = sessionStorage.getItem('session_revoked_reason');
      if (reason) {
        setSessionRevokedReason(reason);
        sessionStorage.removeItem('session_revoked_reason');
      }
    } catch (_) { /* ignore */ }
  }, []);

  const loadCaptcha = async () => {
    try {
      const response = await fetch(`${API}/security/captcha`);
      const data = await response.json();
      if (data.captcha_required) {
        setCaptcha(data);
      }
    } catch (error) {
      console.error('Error loading CAPTCHA:', error);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setForgotLoading(true);

    try {
      const response = await fetch(`${API}/auth/password-reset-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setForgotSuccess(true);
      } else {
        setError(data.detail || t('error_login'));
      }
    } catch (error) {
      setError(t('error_network'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (captcha && captcha.captcha_required) {
        const captchaVerify = await fetch(`${API}/security/verify-captcha`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            question_id: captcha.question_id,
            answer: formData.captchaAnswer
          })
        });
        const captchaResult = await captchaVerify.json();

        if (!captchaResult.valid) {
          setError(t('error_captcha'));
          loadCaptcha();
          setFormData({...formData, captchaAnswer: ''});
          setLoading(false);
          return;
        }
      }

      const formDataToSend = new FormData();
      formDataToSend.append('email', formData.email);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('remember_me', formData.rememberMe);

      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        body: formDataToSend,
        headers: { ...deviceHeader() },
      });

      const data = await response.json();

      if (response.ok) {
        // Persist the canonical device_id returned by the backend (in case it created a fresh one)
        if (data.device_id) setDeviceId(data.device_id);
        localStorage.setItem('user_token', data.access_token);
        localStorage.setItem('user_data', JSON.stringify(data.user));
        localStorage.setItem('remember_me', data.remember_me || false);
        onSuccess(data.user, data.access_token);
      } else if (response.status === 423) {
        // Hit the device cap. Show the modal AND a clear inline fallback message
        // (in case the modal can't render for any reason, the user still understands).
        const cap = data?.detail?.device_limit || 3;
        const list = data?.detail?.active_devices || [];
        setError(`You're already signed in on ${cap} devices. Sign out one of them to continue, or contact support if this wasn't you.`);
        if (data?.detail?.code === 'device_limit_reached' && list.length > 0) {
          setActiveDevices(list);
          setDeviceLimit(cap);
          setDeviceLimitOpen(true);
        }
      } else if (response.status === 403 && data?.detail && typeof data.detail === 'object' && (data.detail.code === 'banned' || data.detail.code === 'suspended')) {
        // Banned / suspended account — store info + redirect to lockout page
        sessionStorage.setItem('lockout_info', JSON.stringify(data.detail));
        sessionStorage.setItem('lockout_email', formData.email);
        window.location.href = data.detail.code === 'banned' ? '/account-banned' : '/account-suspended';
      } else {
        const detailMsg = typeof data.detail === 'string' ? data.detail : (data.detail?.message || t('error_login'));
        setError(detailMsg);
        if (captcha) loadCaptcha();
      }
    } catch (error) {
      setError(t('error_network'));
    } finally {
      setLoading(false);
    }
  };

  // Called by DeviceLimitModal after a successful kick + retry login
  const handleDeviceLimitConfirm = (data) => {
    if (data.device_id) setDeviceId(data.device_id);
    localStorage.setItem('user_token', data.access_token);
    localStorage.setItem('user_data', JSON.stringify(data.user));
    localStorage.setItem('remember_me', data.remember_me || false);
    setDeviceLimitOpen(false);
    onSuccess(data.user, data.access_token);
  };

  // Style helpers: dc = custom design colors, otherwise classic Tailwind
  const labelStyle = dc
    ? { color: dc.labelColor }
    : undefined;
  const labelClass = dc
    ? 'block text-sm font-medium mb-1.5'
    : `block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const iconStyle = dc
    ? { color: dc.iconColor }
    : undefined;
  const iconClass = dc
    ? 'absolute left-3 top-1/2 -translate-y-1/2'
    : `absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`;

  const inputStyle = dc
    ? { backgroundColor: dc.inputBg, borderColor: dc.inputBorder, color: dc.inputText }
    : undefined;
  const inputClass = dc
    ? 'w-full pl-10 pr-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
    : `w-full pl-10 pr-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'}`;

  const inputPassClass = dc
    ? 'w-full pl-10 pr-12 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
    : `w-full pl-10 pr-12 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'}`;

  const secondaryStyle = dc
    ? { color: dc.secondaryText }
    : undefined;

  const linkStyle = dc
    ? { color: dc.linkColor }
    : undefined;

  const buttonStyle = dc
    ? { background: dc.buttonBg, color: dc.buttonText }
    : undefined;

  // Unique ID for placeholder styling
  const placeholderId = dc ? 'login-dc-inputs' : '';

  return (
    <div>
      {/* Scoped placeholder color for custom designs */}
      {dc && (
        <style>{`
          .${placeholderId} input::placeholder { color: ${dc.inputPlaceholder} !important; }
        `}</style>
      )}

      {sessionRevokedReason && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-2">
          <span className="text-base">⚠️</span>
          <div className="flex-1">
            <div className="font-semibold">{t('session_ended') || 'Your session ended'}</div>
            <div className="text-xs mt-0.5">{sessionRevokedReason}</div>
            <div className="text-xs mt-1 opacity-80">{t('if_not_you_change_password') || 'If this wasn\'t you, change your password after signing in.'}</div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className={`space-y-4 ${placeholderId}`} autoComplete="off">
        <input type="text" name="website" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />

        {/* Email */}
        <div>
          <label className={labelClass} style={labelStyle}>
            {t('email')}
          </label>
          <div className="relative">
            <div className={iconClass} style={iconStyle}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className={inputClass}
              style={inputStyle}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>
        </div>

        {/* Password */}
        <div>
          <label className={labelClass} style={labelStyle}>
            {t('password')}
          </label>
          <div className="relative">
            <div className={iconClass} style={iconStyle}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <input
              type={showPassword ? 'text' : 'password'}
              required
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className={inputPassClass}
              style={inputStyle}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={dc ? 'absolute right-3 top-1/2 -translate-y-1/2' : `absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
              style={iconStyle}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* CAPTCHA */}
        {captcha && captcha.captcha_required && (
          <div
            className={dc ? 'p-4 rounded-xl border' : `p-4 rounded-xl border ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-blue-50/50 border-blue-100'}`}
            style={dc ? { backgroundColor: dc.inputBg, borderColor: dc.inputBorder } : undefined}
          >
            <label className={labelClass} style={labelStyle}>
              {t('security_question')}
            </label>
            <p className={dc ? 'text-sm mb-2' : `text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} style={secondaryStyle}>
              {captcha.question}
            </p>
            <input
              type="text"
              required
              value={formData.captchaAnswer}
              onChange={(e) => setFormData({...formData, captchaAnswer: e.target.value})}
              className={dc
                ? 'w-full px-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                : `w-full px-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-600/50 border-gray-500 text-white placeholder-gray-500' : 'bg-white border-gray-200 placeholder-gray-400'}`}
              style={inputStyle}
              placeholder={t('your_answer')}
            />
          </div>
        )}

        {/* Remember Me + Forgot Password */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={formData.rememberMe}
              onChange={(e) => setFormData({...formData, rememberMe: e.target.checked})}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              style={dc ? { accentColor: dc.linkColor } : undefined}
            />
            <label
              htmlFor="rememberMe"
              className={dc ? 'ml-2 text-sm' : `ml-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
              style={secondaryStyle}
            >
              {t('remember_me')}
            </label>
          </div>
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className={dc ? 'text-sm font-medium' : 'text-sm text-blue-600 hover:text-blue-700 font-medium'}
            style={linkStyle}
          >
            {t('forgot_password')}
          </button>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className={dc
            ? 'w-full py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 shadow-lg'
            : 'w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold text-sm hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 shadow-lg shadow-blue-500/25'}
          style={buttonStyle}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('signing_in')}
            </span>
          ) : t('sign_in')}
        </button>
      </form>

      {/* Switch to register */}
      {!registerHidden && (
        <p
          className={dc ? 'mt-6 text-center text-sm' : `mt-6 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
          style={secondaryStyle}
        >
          {t('no_account')}{' '}
          <button
            onClick={onSwitchToRegister}
            className={dc ? 'font-semibold' : 'text-blue-600 hover:text-blue-700 font-semibold'}
            style={linkStyle}
          >
            {t('create_one')}
          </button>
        </p>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full p-6 rounded-2xl shadow-2xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('reset_password')}</h3>
              <button
                onClick={() => { setShowForgotPassword(false); setForgotEmail(''); setForgotSuccess(false); setError(''); }}
                className={`p-1 rounded-lg transition-colors ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {forgotSuccess ? (
              <div>
                <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl">
                  <p className="font-semibold mb-1">{t('email_sent')}</p>
                  <p className="text-sm">{t('email_sent_desc')}</p>
                </div>
                <button
                  onClick={() => { setShowForgotPassword(false); setForgotEmail(''); setForgotSuccess(false); }}
                  className="w-full py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium text-sm"
                >{t('close')}</button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('reset_password_desc')}
                </p>
                {error && <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm">{error}</div>}
                <div className="relative">
                  <div className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setForgotEmail(''); setError(''); }}
                    className={`flex-1 py-3 rounded-xl font-medium text-sm ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >{t('cancel')}</button>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 font-medium text-sm disabled:opacity-50"
                  >{forgotLoading ? t('sending') : t('send_reset_link')}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <DeviceLimitModal
        open={deviceLimitOpen}
        devices={activeDevices}
        deviceLimit={deviceLimit}
        email={formData.email}
        password={formData.password}
        API={API}
        onCancel={() => setDeviceLimitOpen(false)}
        onConfirm={handleDeviceLimitConfirm}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default UserLogin;
